import { Button, FlexContainer, LoadingPage } from '@act/storybook';
import type { ReportTemplateDto } from '@act/shared/data-transfer-objects';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { reportsRequests } from 'redux/requests/reports.requests';
import { useApiCall } from 'redux/utils/apiHook.hook';
import { useToast } from 'react-components/app/ReusableToast';
import ReportsPageHeader from '../components/ReportsPageHeader';
import ReportsSurface from '../components/ReportsSurface';
import { useReportsPermissions } from '../hooks/useReportsPermissions.hook';
import ReportsUnauthorized from '../components/ReportsUnauthorized';
import { formatReportTimestamp } from '../utils/reportDisplay.util';

/**
 * Template list with rename and delete actions.
 */
const TemplatesPage = () => {
  const toast = useToast();
  const { canEdit } = useReportsPermissions();
  const [renameId, setRenameId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');

  const [templates, loading, loadTemplates] = useApiCall(
    reportsRequests.listTemplates,
  );
  const [updatedTemplate, updating, updateTemplate] = useApiCall(
    reportsRequests.updateTemplate,
  );
  const [, deleting, deleteTemplate] = useApiCall(
    reportsRequests.deleteTemplate,
  );

  useEffect(() => {
    loadTemplates({ params: {} });
  }, [loadTemplates]);

  useEffect(() => {
    if (updatedTemplate && !updating) {
      toast('Template renamed', 'success');
      setRenameId(null);
      loadTemplates({ params: {} });
    }
  }, [updatedTemplate, updating, loadTemplates, toast]);

  if (!canEdit) {
    return <ReportsUnauthorized />;
  }

  if (loading) {
    return <LoadingPage />;
  }

  const startRename = (template: ReportTemplateDto) => {
    setRenameId(template.id);
    setRenameValue(template.name);
  };

  const submitRename = (templateId: string) => {
    if (!renameValue.trim()) {
      return;
    }
    updateTemplate({
      params: { id: templateId },
      body: { name: renameValue.trim() },
    });
  };

  const handleDelete = (template: ReportTemplateDto) => {
    deleteTemplate({ params: { id: template.id } });
    toast(`Deleted template "${template.name}"`, 'success');
    loadTemplates({ params: {} });
  };

  return (
    <FlexContainer
      direction="column"
      gap={2}
      width="100%"
      height="auto"
      overflowAllVisible={true}
    >
      <ReportsPageHeader
        title="Templates"
        subtitle="Reusable section order and boilerplate. Save new templates from a report builder."
      />

      {(templates ?? []).length === 0 ? (
        <ReportsSurface padding={3}>
          <Typography color="text.secondary">No templates yet.</Typography>
        </ReportsSurface>
      ) : (
        <ReportsSurface padding={0}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Cadence</TableCell>
              <TableCell>Sections</TableCell>
              <TableCell>Updated</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(templates ?? []).map(template => (
              <TableRow key={template.id}>
                <TableCell>
                  {renameId === template.id ? (
                    <FlexContainer direction="row" gap={1} alignItems="center">
                      <TextField
                        size="small"
                        value={renameValue}
                        onChange={event => setRenameValue(event.target.value)}
                      />
                      <Button
                        size="small"
                        disabled={updating}
                        trackingName="reports-template-rename-save"
                        onClick={() => submitRename(template.id)}
                      >
                        Save
                      </Button>
                      <Button
                        size="small"
                        variant="text"
                        trackingName="reports-template-rename-cancel"
                        onClick={() => setRenameId(null)}
                      >
                        Cancel
                      </Button>
                    </FlexContainer>
                  ) : (
                    template.name
                  )}
                </TableCell>
                <TableCell style={{ textTransform: 'capitalize' }}>
                  {template.cadence}
                </TableCell>
                <TableCell>{template.sectionOrder.length}</TableCell>
                <TableCell>{formatReportTimestamp(template.updatedAt)}</TableCell>
                <TableCell align="right">
                  <FlexContainer direction="row" gap={1} justifyContent="flex-end">
                    <Button
                      variant="text"
                      size="small"
                      trackingName="reports-template-rename"
                      onClick={() => startRename(template)}
                    >
                      Rename
                    </Button>
                    <Button
                      variant="text"
                      size="small"
                      color="error"
                      disabled={deleting}
                      trackingName="reports-template-delete"
                      onClick={() => handleDelete(template)}
                    >
                      Delete
                    </Button>
                  </FlexContainer>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        </ReportsSurface>
      )}
    </FlexContainer>
  );
};

export default TemplatesPage;
